import { TourStartPage } from '@app/pages/tour-start-page/TourStartPage';
import { Button, LinearProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilCallback } from 'recoil';
import { atom_tourState, atom_isDesktop } from '@app/shared/state/tour.atom';
import { atom_mapRef } from '@app/shared/state/map-ref.atom';
import { atom_credentialItemsState, saveCredentials } from '@app/shared/state/credential-items.atom';
// OpeningStopBlock retired — content now lives in the nav-start CTA built in useRouteTracker.ts.
// import { OpeningStopBlock } from '@app/features/map/opening-stop-block/OpeningStopBlock';
import { motion, AnimatePresence, HTMLMotionProps } from 'framer-motion';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_tourStage, atom_loadingProgress, LoadingPhase } from '@app/shared/state/loading.atom';
import { BigStopPage } from '@app/features/map/big-stop-page/BigStopPage';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { useTranslation } from 'react-i18next';
import { getDeviceId } from '@app/shared/utils/device-id';
import { deleteAllCachesWithRetry, deleteAllDatabases } from '@app/pages/delete-dbs';
import { useParams, useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { atom_savedStopState } from "@app/shared/state/saved-stop-state.atom";
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_tourNotifications } from '@app/shared/state/notification.atom';
import { Notifications } from '@app/shared/components/Notifications';
import { Z_INDEX } from '@app/shared/styles/zIndex';
import { useRenderTracker } from '@app/shared/debug';


const framerSlideLeft: HTMLMotionProps<'div'> = {
	transition: { duration: 0.3, ease: 'easeInOut' },
	initial: { translateX: '100vw' },
	animate: { translateX: '0vw' },
	exit: { translateX: '-100vw' }
};

const framerSlideUpDown: HTMLMotionProps<'div'> = {
	transition: { duration: 0.2, ease: 'easeInOut' },
	initial: { translateY: '100vh' },
	animate: { translateY: '0vh' },
	exit: { translateY: '100vh' }
};

// ─── Init logic: credentials, validation, language, notifications, initial active stop ───
const TourInitLogic = React.memo(function TourInitLogic() {
	useRenderTracker('TourInitLogic');
	const { i18n } = useTranslation();
	const { tourId } = useParams();
	const state_tourState = useRecoilValue(atom_tourState) as any;
	const [tourStage, setTourStage] = useRecoilState(atom_tourStage);
	const setLoadingProgress = useSetRecoilState(atom_loadingProgress);
	const setTourNotifications = useSetRecoilState(atom_tourNotifications);
	const notificationsFetchedRef = React.useRef(false);

	const queryParams = new URLSearchParams(location.search);
	const uID = queryParams.get('uID');
	const gID = queryParams.get('gID');
	const taID = queryParams.get('taID') as string;
	if (!!Number(taID) && state_tourState?.id) {
		localStorage.setItem('tasteCount_' + state_tourState?.id, taID);
	}

	// Save credentials
	useEffect(() => {
		if (gID && uID && tourId) {
			saveCredentials({ gID, uID, tID: tourId });
		}
	}, [gID, uID, state_tourState?.id]);

	// Initialize language from tour data
	useEffect(() => {
		if (state_tourState?.language) {
			const mapLangToLangCode = {
				Hebrew: 'he',
				hebrew: 'he',
				English: 'en',
				english: 'en',
				Spanish: 'esp',
				spanish: 'esp',
				German: 'de',
				german: 'de',
				French: 'fr',
				french: 'fr',
				Portuguese: 'pt',
				portuguese: 'pt'
			};
			const langCode = mapLangToLangCode[state_tourState.language as keyof typeof mapLangToLangCode];
			(i18n as any).changeLanguage(langCode);
			document.dir = state_tourState.language === 'Hebrew' ? 'rtl' : 'ltr';
		}
	}, [state_tourState?.language, i18n]);

	// (Phase 1 restore — initial active stop — moved to StateRestorer)

	// Check if returning user — skip start page
	useEffect(() => {
		if (state_tourState?.id) {
			const firstVisitKey = 'first_visit_' + state_tourState?.id;
			const hasVisitedBefore = localStorage.getItem(firstVisitKey);
			if (hasVisitedBefore === 'true') {
				setTourStage('map');
			}
		}
	}, [state_tourState?.id]);

	// Validate tour access
	useEffect(() => {
		const validateTour = async () => {
			const uniqueID = getDeviceId();
			const data = JSON.parse(localStorage.getItem('credential_items') || '{}');
			try {
				setLoadingProgress({ phase: 'validating_access', percent: 80 });
				const response = await fetch(
					`https://europe-west3-tours-app-1579553856346.cloudfunctions.net/validateWebUsage1?tID=${state_tourState?.id}&gID=${data.gID}&uID=${data.uID}&uniqueID=${uniqueID}`
				);
				const result = await response.json();
				setLoadingProgress({ phase: 'validating_access', percent: 95 });

				if ((result.expiresAt && new Date() > new Date(result.expiresAt)) || !result.allowed) {
					// Tour expired — TourLoadingScreen will handle via tourStage
					return;
				}

				setLoadingProgress({ phase: 'preparing_tour', percent: 95 });
				await new Promise(resolve => setTimeout(resolve, 300));
				setLoadingProgress({ phase: 'preparing_tour', percent: 100 });
			} catch (error) {
				console.error('Tour validation error:', error);
				setLoadingProgress({ phase: 'preparing_tour', percent: 100 });
			} finally {
				const firstVisitKey = 'first_visit_' + state_tourState?.id;
				const hasVisitedBefore = localStorage.getItem(firstVisitKey) === 'true';
				if (state_tourState?.config?.only_map || hasVisitedBefore) {
					setTourStage('map');
				} else {
					setTourStage('startPage');
				}
			}
		};

		if (state_tourState?.id) {
			validateTour();
		}
	}, [state_tourState?.id]);

	// Fetch notifications
	useEffect(() => {
		if (tourStage === 'loading') return;
		if (notificationsFetchedRef.current) return;
		if (!state_tourState?.id) return;

		notificationsFetchedRef.current = true;

		let uID: string | undefined;
		try {
			const credentials = JSON.parse(localStorage.getItem('credential_items') || '{}');
			uID = credentials?.uID;
		} catch {
			console.warn('[TourSummaryOverlay] Failed to parse credentials');
			return;
		}
		if (!uID) {
			console.warn('[TourSummaryOverlay] No uID - skipping notification fetch');
			return;
		}

		const fetchNotifications = async () => {
			const baseUrl = `https://get-notifications-for-tour-613221148836.europe-west1.run.app?tourId=${encodeURIComponent(state_tourState.id)}&uID=${encodeURIComponent(uID as string)}`;
			const freshUrl = `${baseUrl}&_t=${Date.now()}`;
			if (navigator.onLine) {
				try {
					const response = await fetch(freshUrl, {
						method: 'GET',
						headers: { 'Content-Type': 'application/json' },
						cache: 'no-store',
					});
					if (!response.ok) throw new Error(`HTTP ${response.status}`);
					const data = await response.json();
					const notifications = Array.isArray(data) ? data : (data?.notifications || []);
					setTourNotifications(notifications);
					if ('caches' in window) {
						try {
							const cache = await caches.open('firebase-api-cache');
							await cache.delete(baseUrl);
							const cacheResponse = new Response(JSON.stringify(notifications), {
								headers: { 'Content-Type': 'application/json' },
							});
							await cache.put(baseUrl, cacheResponse);
						} catch (cacheErr) {
							console.warn('[Notifications] Failed to update cache:', cacheErr);
						}
					}
					return;
				} catch (err) {
					console.warn('[Notifications] Network failed:', err);
				}
			}

			try {
				if ('caches' in window) {
					const cache = await caches.open('firebase-api-cache');
					const cachedResponse = await cache.match(baseUrl);
					if (cachedResponse) {
						const data = await cachedResponse.json();
						const notifications = Array.isArray(data) ? data : (data?.notifications || []);
						setTourNotifications(notifications);
						return;
					}
				}
			} catch (err) {
				console.warn('[Notifications] Cache failed:', err);
			}

			setTourNotifications([]);
		};

		fetchNotifications();
	}, [tourStage, state_tourState?.id, setTourNotifications]);

	return null;
});


// ─── Loading screen: progress bar + validation UI ───
const TourLoadingScreen = React.memo(function TourLoadingScreen() {
	useRenderTracker('TourLoadingScreen');
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [tourStage] = useRecoilState(atom_tourStage);
	const [loadingProgress] = useRecoilState(atom_loadingProgress);
	const state_tourState = useRecoilValue(atom_tourState) as any;
	const [showReloadButton, setShowReloadButton] = useState(false);
	const [tourExpiredText, setTourExpiredText] = useState("");
	const [isAllowed, setIsAllowed] = useState(true);

	useEffect(() => {
		let reloadTimeout: NodeJS.Timeout;
		let autoReloadTimeout: NodeJS.Timeout;

		if (tourStage === 'loading') {
			reloadTimeout = setTimeout(() => setShowReloadButton(true), 15000);
			autoReloadTimeout = setTimeout(async () => {
				await Promise.race([
					deleteAllDatabases(),
					deleteAllCachesWithRetry(),
					new Promise((resolve) => setTimeout(resolve, 1000))
				]);
				window.location.reload();
			}, 20000);
		}

		return () => {
			clearTimeout(reloadTimeout);
			clearTimeout(autoReloadTimeout);
		};
	}, [tourStage]);

	// Check allowed
	useEffect(() => {
		const handleIsAllowed = async () => {
			if (!state_tourState?.id) return;
			if (localStorage.getItem('allowed_' + state_tourState?.id)) {
				setIsAllowed(true);
				return;
			}
			const uniqueID = getDeviceId();
			const data = JSON.parse(localStorage.getItem('credential_items') || '{}');
			const allowed = await fetch(`https://europe-west3-tours-app-1579553856346.cloudfunctions.net/validateWebUsage1?tID=${state_tourState?.id}&gID=${data.gID}&uID=${data.uID}&uniqueID=${uniqueID}`)
				.then(res => res.json());
			if (allowed) {
				setIsAllowed(true);
				localStorage.setItem('allowed_' + state_tourState?.id, 'true');
			}
		};
		handleIsAllowed();
	}, [state_tourState?.id]);

	const getPhaseLabel = (phase: LoadingPhase): string => {
		const phaseKeys: Record<LoadingPhase, string> = {
			fetching_tour: 'pre_start_tour.loading_phases.fetching_tour',
			syncing_user_data: 'pre_start_tour.loading_phases.syncing_user_data',
			loading_boundaries: 'pre_start_tour.loading_phases.loading_boundaries',
			validating_access: 'pre_start_tour.loading_phases.validating_access',
			preparing_tour: 'pre_start_tour.loading_phases.preparing_tour',
		};
		return t(phaseKeys[phase]);
	};

	if (tourStage !== 'loading' && !tourExpiredText) return null;

	return (
		<>
			{tourExpiredText && (
				<Box
					sx={{
						position: 'fixed',
						top: 0, left: 0, right: 0, bottom: 0,
						backgroundColor: '#FFFFFF',
						display: 'flex', justifyContent: 'center', alignItems: 'center',
						zIndex: 99999,
					}}
				>
					<Box sx={{ width: '100%', maxWidth: 420, p: 3, display: 'flex', flexDirection: 'column', textAlign: 'center', minHeight: '100vh' }}>
						<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: '10vh' }}>
							<Box sx={{ width: 88, height: 88, padding: '20px', backgroundColor: '#FBE9DD', borderRadius: '44px', mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
								<LockOutlinedIcon sx={{ fontSize: 48, color: '#E65100' }} />
							</Box>
							<Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: '22px', color: '#000', mb: 1, textAlign: 'center' }}>
								{tourExpiredText || t('tour_expired')}
							</Typography>
							<Typography sx={{ fontSize: 16, fontWeight: 400, lineHeight: '24px', color: '#6B7280', mb: 4, textAlign: 'center', maxWidth: 300 }}>
								{t('tour_expired_description')}
							</Typography>
							<Button
								variant="contained"
								onClick={() => {
									try {
										const creds = JSON.parse(localStorage.getItem('credential_items') || '{}');
										const uid = creds.uID;
										const gid = creds.gID;
										if (uid) {
											navigate(`/mytours/${encodeURIComponent(uid)}${gid ? `?gID=${encodeURIComponent(gid)}` : ''}`);
										} else {
											navigate('/mytours');
										}
									} catch {
										navigate('/mytours');
									}
								}}
								sx={{ backgroundColor: '#E65100', color: '#fff', borderRadius: '24px', py: 1.5, px: 4, fontSize: 14, fontWeight: 600, textTransform: 'none', boxShadow: 'none', '&:hover': { backgroundColor: '#D34600', boxShadow: 'none' } }}
							>
								{t('tour_expired_back')}
							</Button>
						</Box>
						<Box sx={{ textAlign: 'center', pb: 2 }}>
							<Typography sx={{ fontSize: 14, color: '#6B7280' }}>
								{t('my_tours.login.need_help') || 'Need help?'}{' '}
								<a href="mailto:support@d-guide.com" style={{ color: '#E65100', fontWeight: 600, textDecoration: 'none' }}>
									{t('my_tours.login.contact_us') || 'Contact Us'}
								</a>
							</Typography>
						</Box>
					</Box>
				</Box>
			)}
			{tourStage === 'loading' && (
				<Box
					className="fixed top-0 bottom-0 w-screen h-screen bg-white overflow-auto flex flex-col justify-center items-center"
					sx={{ zIndex: 99999 }}
				>
					<Typography variant="h5" className="mb-4" sx={{ fontWeight: 500 }}>
						{getPhaseLabel(loadingProgress.phase)}
					</Typography>
					<Box sx={{ width: '80%', maxWidth: 400, mb: 2 }}>
						<LinearProgress
							variant="determinate"
							value={loadingProgress.percent}
							sx={{
								height: 8, borderRadius: 4, backgroundColor: 'rgba(0, 0, 0, 0.1)',
								'& .MuiLinearProgress-bar': { borderRadius: 4, transition: 'transform 0.3s ease-in-out' },
							}}
						/>
					</Box>
					<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
						{loadingProgress.percent}%
					</Typography>
					{showReloadButton && (
						<div style={{ direction: 'ltr', textAlign: 'center' }}>
							<div style={{ display: 'flex', flexDirection: 'column', margin: '24px 0', justifyContent: 'center', alignItems: 'center' }}>
								<Typography variant="body1" style={{ marginBottom: '8px' }}>{t('pre_start_tour.reload_tour_title')}</Typography>
								<Typography variant="body2" color="textSecondary">{t('pre_start_tour.reload_tour_description')}</Typography>
							</div>
							<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
								<Button
									variant="contained"
									onClick={async () => {
										await Promise.race([deleteAllDatabases(), deleteAllCachesWithRetry(), new Promise((resolve) => setTimeout(resolve, 1000))]);
										window.location.reload();
									}}
								>
									{t('pre_start_tour.reload_tour')}
								</Button>
							</div>
						</div>
					)}
				</Box>
			)}
		</>
	);
});


// ─── Start page overlay ───
const TourStartOverlay = React.memo(function TourStartOverlay() {
	useRenderTracker('TourStartOverlay');
	const { t } = useTranslation();
	const [tourStage, setTourStage] = useRecoilState(atom_tourStage);
	const state_tourState = useRecoilValue(atom_tourState) as any;
	const isDesktop = useRecoilValue(atom_isDesktop);
	const setSideBarState = useSetRecoilState(sideBarStateAtom);
	const setListMode = useSetRecoilState(atom_listModeState);
	const [isAllowed, setIsAllowed] = useState(true);

	const queryParams = new URLSearchParams(location.search);
	const autoDownload = queryParams.get('autoDownload') === 'true';

	// Check allowed
	useEffect(() => {
		const handleIsAllowed = async () => {
			if (!state_tourState?.id) return;
			if (localStorage.getItem('allowed_' + state_tourState?.id)) {
				setIsAllowed(true);
				return;
			}
			const uniqueID = getDeviceId();
			const data = JSON.parse(localStorage.getItem('credential_items') || '{}');
			const allowed = await fetch(`https://europe-west3-tours-app-1579553856346.cloudfunctions.net/validateWebUsage1?tID=${state_tourState?.id}&gID=${data.gID}&uID=${data.uID}&uniqueID=${uniqueID}`)
				.then(res => res.json());
			if (allowed) {
				setIsAllowed(true);
				localStorage.setItem('allowed_' + state_tourState?.id, 'true');
			}
		};
		handleIsAllowed();
	}, [state_tourState?.id]);

	if (tourStage !== 'startPage' || !state_tourState) return null;

	return (
		<motion.div
			id="tour-start-page"
			className="fixed top-0 bottom-0 w-screen h-screen bg-white overflow-auto"
			style={{ zIndex: 999, opacity: 1 }}
			transition={{ ...framerSlideLeft, initial: { translateX: '0vw' } }}
		>
			{isAllowed ? (
				<TourStartPage
					autoDownload={autoDownload}
					onClickStart={() => {
						document.getElementById("tour-start-page")?.scrollTo(0, 0);
						setSideBarState(null);
						const defaultListMode = isDesktop
							? 'desktop'
							: state_tourState?.config?.['show_list']
								? 'mobileList'
								: 'mobileSwiper';
						setListMode(defaultListMode);
						if (state_tourState?.id) {
							const firstVisitKey = 'first_visit_' + state_tourState?.id;
							localStorage.setItem(firstVisitKey, 'true');
						}
						// 'openingStop' stage retired — its content now appears as a CTA inside the map.
						// Always go straight to 'map'; useRouteTracker fires the welcome CTA when nav starts.
						setTourStage('map');
					}}
					tour={state_tourState}
					className=""
				/>
			) : (
				<Box className="flex justify-center ">
					<Typography style={{ height: '80vh', display: 'flex', alignItems: 'center' }}>
						YOU ARE NOT ALLOWED TO ACCESS THIS PAGE
					</Typography>
				</Box>
			)}
		</motion.div>
	);
});


// ─── Content overlay: OpeningStop + Notifications (BigStopPage moved to MapPage) ───
// Isolated: only re-renders when sideBarState or isDesktop changes
const MobileNotificationsGate = React.memo(function MobileNotificationsGate() {
	const sideBarState = useRecoilValue(sideBarStateAtom);
	const isDesktop = useRecoilValue(atom_isDesktop);

	if (isDesktop || sideBarState !== 'notifications') return null;

	return (
		<motion.div
			key="notifications-page"
			className="fixed top-0 bottom-0 w-screen bg-white overflow-auto"
			style={{ zIndex: 1000, height: '100dvh' }}
			{...framerSlideUpDown}
		>
			<Notifications />
		</motion.div>
	);
});

// OpeningStopGate retired — welcome content now appears as the nav-start CTA (see useRouteTracker.ts).
// const OpeningStopGate = React.memo(function OpeningStopGate({ onSkip }: { onSkip: () => void }) {
// 	const state_tourState = useRecoilValue(atom_tourState) as any;
// 	if (!state_tourState) return null;
//
// 	return (
// 		<motion.div
// 			className="fixed top-0 bottom-0 w-screen h-screen bg-white overflow-auto"
// 			style={{ zIndex: 1000, height: 1000 }}
// 			{...framerSlideLeft}
// 		>
// 			<OpeningStopBlock
// 				stop={state_tourState.stops[0]}
// 				onClickSkip={onSkip}
// 			/>
// 		</motion.div>
// 	);
// });

const TourContentOverlay = React.memo(function TourContentOverlay() {
	useRenderTracker('TourContentOverlay');
	const tourStage = useRecoilValue(atom_tourStage);

	return (
		<AnimatePresence>
			{/* 'openingStop' stage retired — replaced by the nav-start CTA. */}
			{/* {tourStage === 'openingStop' && <OpeningStopGate onSkip={() => setTourStage('map')} />} */}

			{/* Mobile Notifications Page - isolated to avoid re-renders from sideBarState */}
			{tourStage === 'map' && <MobileNotificationsGate />}
		</AnimatePresence>
	);
});

// ─── BigStopOverlay: independent, only renders when a big stop is opened ───
export const BigStopOverlay = React.memo(function BigStopOverlay() {
	useRenderTracker('BigStopOverlay');
	const [state_openedStopState] = useRecoilState(atom_openedStopState);
	const isDesktop = useRecoilValue(atom_isDesktop);
	const mapRef = useRecoilValue(atom_mapRef) as any;

	// Measure the actual rendered desktop sidebar so BigStopPage can dock beside it
	// without math guesswork. Falls back to 0 on mobile (no sidebar).
	const [sidebarRight, setSidebarRight] = useState(0);
	// BigStopPage width follows the same formula as the sidebar's list panel (sidebarWidthPx)
	const [sidebarListWidth, setSidebarListWidth] = useState(0);
	useEffect(() => {
		const measure = () => {
			const el = document.querySelector('[data-desktop-sidebar]') as HTMLElement | null;
			if (!el) { setSidebarRight(0); return; }
			const rect = el.getBoundingClientRect();
			setSidebarRight(Math.round(rect.right));
		};
		const handleResize = () => {
			const width = typeof window !== 'undefined' ? window.innerWidth : 0;
			setSidebarListWidth(Math.max(Math.floor(width >= 1400 ? width * 0.25 : width >= 1100 ? width * 0.33 : width >= 1000 ? width * 0.4 : (width - 85) / 2), 400));
			measure();
		};
		handleResize();
		const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
		const el = document.querySelector('[data-desktop-sidebar]') as HTMLElement | null;
		if (ro && el) ro.observe(el);
		window.addEventListener('resize', handleResize);
		// Re-measure once more after animations settle (sidebar may mount after us)
		const t = setTimeout(measure, 200);
		return () => {
			window.removeEventListener('resize', handleResize);
			ro?.disconnect();
			clearTimeout(t);
		};
	}, [isDesktop, state_openedStopState?.id]);

	// When BigStopPage opens on desktop (user-initiated), re-center the active stop into the
	// visible map area. Skip only on the very first restore (savedStopState match) — the initial
	// flyTo there already applies the offset and takes ~3s, so panning would interrupt it.
	const savedStopState = useRecoilValue(atom_savedStopState);
	const restoreHandledRef = useRef(false);
	useEffect(() => {
		if (!isDesktop || !state_openedStopState || !mapRef || sidebarListWidth <= 0) return;
		// On the first open after mount, if it matches savedStopState, treat as restore and skip
		if (
			!restoreHandledRef.current &&
			savedStopState?.stopId === state_openedStopState.id &&
			savedStopState?.modalType === 'bigStop'
		) {
			restoreHandledRef.current = true;
			return;
		}
		restoreHandledRef.current = true;
		const lat = state_openedStopState.stop_location?.latitude;
		const lng = state_openedStopState.stop_location?.longitude;
		if (typeof lat !== 'number' || typeof lng !== 'number') return;
		// Small delay so the slide-in animation starts and the layout is stable
		const t = setTimeout(() => {
			try {
				const zoom = mapRef.getZoom?.();
				if (typeof zoom !== 'number') return;
				const point = mapRef.project([lat, lng], zoom);
				point.x -= sidebarListWidth / 2; // shift west so marker appears east of container center
				const newCenter = mapRef.unproject(point, zoom);
				mapRef.panTo([newCenter.lat, newCenter.lng], { animate: true, duration: 0.4 });
			} catch { /* ignore */ }
		}, 350);
		return () => clearTimeout(t);
	}, [isDesktop, state_openedStopState?.id, mapRef, sidebarListWidth, savedStopState]);

	if (!state_openedStopState) {
		return null;
	}

	// Portal to document.body so this overlay competes in the root stacking context
	// alongside other portaled UI (MapLocationButton). Without this, BigStopPage's
	// z-index would only be local to MapPage's stacking context, never reaching the
	// floating buttons portaled to body.
	return ReactDOM.createPortal(
		<AnimatePresence>
			<motion.div
				key={state_openedStopState?.id || 'opened-stop'}
				className="fixed top-0 bottom-0 bg-white overflow-auto"
				style={{
					zIndex: Z_INDEX.mapOverlay,
					left: isDesktop ? sidebarRight : 0,
					// Clamp width so BigStopPage never extends past the viewport
					width: isDesktop
						? `${Math.min(sidebarListWidth, Math.max(0, (typeof window !== 'undefined' ? window.innerWidth : 0) - sidebarRight))}px`
						: '100vw',
					height: '100dvh',
				}}
				{...(isDesktop ? {
					transition: { duration: 0.3, ease: 'easeInOut' },
					initial: { translateX: '-100%' },
					animate: { translateX: 0 },
					exit: { translateX: '-100%' },
				} : framerSlideUpDown)}
			>
				<BigStopPage
					key={state_openedStopState?.id}
					stop={state_openedStopState}
				/>
			</motion.div>
		</AnimatePresence>,
		document.body,
	);
});


// ─── Main export: just composes the 3 pieces ───
type TourSummaryOverlayProps = {
	children?: React.ReactNode;
};

export const TourSummaryOverlay: React.FC = React.memo<TourSummaryOverlayProps>(
	function TourSummaryOverlay(_p) {
		useRenderTracker('TourSummaryOverlay');
		return (
			<>
				<TourInitLogic />
				<TourLoadingScreen />
				<TourStartOverlay />
				<TourContentOverlay />
			</>
		);
	}
);
